import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { useBidResponseProcessingContext } from "components/bid_module/bid-response/BidResponsesProcessingProvider";
import { useDocumentUploadProgress } from "components/content_library/DocumentUploadProgressProvider";
import UploadCustomerDocumentsForm from "components/content_library/upload_form/UploadCustomerDocumentsForm";
import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import { useCustomerDocuments } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { useUploadCustomerDocuments } from "lib/hooks/api/customer_documents/useUploadCustomerDocuments";
import {
  CustomerDocumentSortBy,
  CustomerDocumentSortOrder,
  CustomerDocumentStatus,
  CustomerDocumentType,
  CustomerDocumentTypeFilter,
} from "lib/types/graphQLEnums";
import { useBid } from "../../../../lib/hooks/api/bid/bids/useBid";
import { EllipsisTooltipTextLink } from "../../../ui/tooltip/EllipsisTooltip";

type Props = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  bidId: string;
  onNavigateToResponses?: () => void;
};

type FormValues = { files: File[] };

function CreateBidResponsesModalBody({
  bidId,
  onOpenChange,
  onNavigateToResponses,
}: Omit<Props, "isOpen">) {
  const { control, watch, reset } = useForm<FormValues>({
    defaultValues: { files: [] },
    mode: "onChange",
  });

  const { files } = watch();

  const { startExtraction, isExtracting } = useBidResponseProcessingContext(bidId);

  const { filesProcessing, startProcessing, uploadStatuses, updateFileStatus } =
    useDocumentUploadProgress(bidId);

  const { data: documents, isLoading: isLoadingDocuments } = useCustomerDocuments(
    {
      resourceId: bidId,
      sortBy: CustomerDocumentSortBy.FileName,
      sortOrder: CustomerDocumentSortOrder.Asc,
      documentType: CustomerDocumentTypeFilter.BidDocument,
    },
    { page: 1, pageSize: 500 },
    { enabled: !!bidId },
  );

  const { mutate: uploadFiles, isLoading: isUploadingFiles } = useUploadCustomerDocuments(
    updateFileStatus,
    CustomerDocumentType.BidDocument,
  );

  const { data: bid } = useBid({ id: bidId });

  const isUploadingOrProcessing = isUploadingFiles || filesProcessing;

  const { readyDocumentIds, hasInFlightDocuments } = useMemo(() => {
    const readyFromQuery = documents
      .filter((d) => d.status === CustomerDocumentStatus.Success)
      .map((d) => d.id);

    const readyFromUploads = uploadStatuses.flatMap((s) =>
      s.documentId && s.isProcessed && !s.isError ? [s.documentId] : [],
    );

    const ready = Array.from(new Set([...readyFromQuery, ...readyFromUploads]));

    const inFlightFromQuery = documents.some(
      (d) =>
        d.status === CustomerDocumentStatus.Processing ||
        d.status === CustomerDocumentStatus.Uploading,
    );

    const inFlightFromUploads = uploadStatuses.some((s) => !s.isFinished && !s.isError);

    return {
      readyDocumentIds: ready,
      hasInFlightDocuments: inFlightFromQuery || inFlightFromUploads,
    };
  }, [documents, uploadStatuses]);

  const canExtractNow =
    readyDocumentIds.length > 0 &&
    !hasInFlightDocuments &&
    !isUploadingOrProcessing &&
    !isExtracting;

  // auto upload when files are added
  const lastUploadedFilesSignatureRef = useRef<string>("");
  useEffect(() => {
    if (files.length === 0) {
      lastUploadedFilesSignatureRef.current = "";
      return;
    }

    if (isUploadingOrProcessing) return;

    const signature = files
      .map((f) => f.name)
      .sort()
      .join("|");
    if (signature === lastUploadedFilesSignatureRef.current) return;

    lastUploadedFilesSignatureRef.current = signature;
    startProcessing(files, bidId);
    uploadFiles({ files, resourceId: bidId, operationId: bidId, uploadStatuses });
  }, [bidId, files, isUploadingOrProcessing, startProcessing, uploadFiles, uploadStatuses]);

  return (
    <Flex flexDirection="column" gap={24}>
      <Flex flexDirection="column" gap={8}>
        <Text h2>Extract from tender documents</Text>
        <Text color="sysTextSecondary">
          We'll use the tender document help set up bid qualification and writing.
        </Text>
      </Flex>

      {bid?.notice?.url && (
        <Flex>
          <Text color="sysTextSecondary">Access tender documents via this link </Text>
          <EllipsisTooltipTextLink
            to={bid.notice.url}
            fullText={bid.notice.url}
            maxWidth={300}
            targetType="new-tab"
          />
        </Flex>
      )}

      <UploadCustomerDocumentsForm
        name="files"
        control={control}
        isLoading={isUploadingOrProcessing}
        maxHeight={200}
        existingDocuments={documents}
      />

      <ButtonContainer>
        <Button
          buttonType="tertiary"
          onClick={() => {
            reset({ files: [] });
            onOpenChange(false);
          }}
        >
          Cancel
        </Button>
        <Button
          buttonType="secondary"
          onClick={() => {
            reset({ files: [] });
            onOpenChange(false);
            onNavigateToResponses?.();
          }}
        >
          Skip
        </Button>
        <Button
          disabled={!canExtractNow}
          loading={isExtracting || isUploadingOrProcessing || isLoadingDocuments}
          onClick={() => {
            startExtraction(readyDocumentIds);
            onOpenChange(false);
            onNavigateToResponses?.();
          }}
        >
          Run extraction
        </Button>
      </ButtonContainer>
    </Flex>
  );
}

export function CreateBidResponsesModal({
  isOpen,
  onOpenChange,
  bidId,
  onNavigateToResponses,
}: Props): JSX.Element {
  return (
    <Modal
      open={isOpen}
      showCloseButton
      onOpenChange={onOpenChange}
      padding={32}
      aria-label="Create bid responses modal"
    >
      <CreateBidResponsesModalBody
        bidId={bidId}
        onOpenChange={onOpenChange}
        onNavigateToResponses={onNavigateToResponses}
      />
    </Modal>
  );
}

const ButtonContainer = styled(Flex)({
  justifyContent: "flex-end",
  gap: 12,
  marginTop: 8,
  flexWrap: "wrap",
});
